/* The one-day sheet — a single day of the tracker, added up.
 *
 * This mirrors the shape of the workbook page rather than the monthly
 * rollup: the same six headline numbers, the per-unit split, every item
 * that ran, and the stoppage, power, waste and stock tables that sit
 * underneath. Reached from the day selector here, or by clicking a cell
 * in the monthly heatmap / a row in the day-by-day register.
 */

import { useMemo, useState } from 'react'
import { production } from '../../lib/api'
import type { DayDetail, MonthSummary, Unit } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import { costPerKg } from '../../lib/cost'
import { compactKg, num, pct } from '../../lib/format'
import { niceMax } from '../../lib/scale'
import {
  Blueprint, Empty, ErrorBox, KpiRow, Loading, Note, PanelHead, Segmented,
} from '../../components/ui'
import { TipRow, TipTitle } from '../../components/charts/hover'

const R: React.CSSProperties = { textAlign: 'right' }
const NUM = 'num'
const BAR_W = 300

const longDate = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

const shortDate = (iso: string, dow: string) =>
  `${new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  })} · ${dow}`

const unitShort = (u: string) => (u === 'UNIT - 1' ? 'U1' : 'U2')
const unitLong = (u: string) => (u === 'UNIT - 1' ? 'Unit 1' : 'Unit 2')

export function DailyTab({ month, unit, date, setDate, goToData }: {
  month: MonthSummary
  unit: Unit | null
  date: string | null
  setDate: (d: string) => void
  goToData: () => void
}) {
  // Default to the most recent day on file for the month in view.
  const selected = date && month.dates.includes(date)
    ? date
    : month.dates[month.dates.length - 1]

  const { data, error, loading, reload } =
    useAsync<DayDetail>(() => production.day(selected, unit), [selected, unit])

  if (error) return <ErrorBox error={error} onRetry={reload} />
  if (loading || !data) return <Loading what={selected} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <DayPicker month={month} selected={selected} setDate={setDate} goToData={goToData} />
      <DailyKpis day={data} bench={month.benchmark} />
      <UnitSplit day={data} />
      <ItemsRun day={data} />
      <ItemBars day={data} />
      <TimeWent day={data} />

      {/* Utilization sits beside the stoppage table on purpose: the stoppage
          percentage booked that day is one of its two factors, so the cause
          and its effect on earned capacity read together. */}
      <section style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 24,
      }}>
        <UtilAndEfficiency day={data} />
        <Stoppages day={data} />
      </section>

      <section style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 24,
      }}>
        <PowerAndWaste day={data} />
        <YarnStock day={data} />
      </section>
    </div>
  )
}

function DayPicker({ month, selected, setDate, goToData }: {
  month: MonthSummary; selected: string; setDate: (d: string) => void; goToData: () => void
}) {
  // Newest first — the day you want is nearly always the most recent one.
  const options = useMemo(
    () => month.dates.slice().reverse().map((d, i) => ({
      key: d,
      label: shortDate(d, month.series.dow[month.dates.length - 1 - i]),
    })),
    [month],
  )
  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <span className="axis-title">Day</span>
      <select value={selected} onChange={(e) => setDate(e.target.value)} style={{ minWidth: 220 }}>
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <button className="btn btn-secondary" onClick={goToData}>Update data</button>
    </div>
  )
}

function DailyKpis({ day, bench }: { day: DayDetail; bench: number }) {
  const t = day.totals
  const actual = t.actual ?? 0
  const eff = t.efficiency ?? 0
  const u = (name: string) => day.unit_totals.find((x) => x.unit === name)
  const u1 = u('UNIT - 1')
  const u2 = u('UNIT - 2')
  const eb = day.electricity.EB ?? 0
  const solar = day.electricity.Solar ?? 0
  // One day's overhead, carried by the unit in view by its output share;
  // power only if the meters were read that day.
  const cost = costPerKg(eb, solar, actual, actual, 1, day.output_share)
  const plant = day.utilization?.rows?.find((r) => r.unit === (day.unit ?? 'Both units'))
  const ue = plant?.util_x_eff ?? null
  // With one unit in view the other unit's tile has nothing to say.
  const showUnit = (name: string) => !day.unit || day.unit === name

  return (
    <KpiRow items={[
      {
        label: 'Actual output', value: num(actual), unit: 'kg',
        note: 'ring frames, all shifts',
      },
      {
        label: 'Util × efficiency', value: ue != null ? ue.toFixed(1) : '–', unit: '%',
        note: ue != null && plant
          ? `${ue >= bench ? 'above' : `${(bench - ue).toFixed(1)} pts under`} the ${bench}% benchmark`
            + ` · ${plant.utilization.toFixed(1)}% util × ${t.efficiency_settings != null ? t.efficiency_settings.toFixed(1) : '–'}% settings eff`
          : 'no output on file',
        tone: ue != null && ue < bench ? 'bad' : undefined,
      },
      {
        label: 'Efficiency · SITRA', value: eff.toFixed(1), unit: '%',
        note: `against SITRA gps × spindles · ${num(t.target ?? 0)} kg target`,
      },
      {
        label: 'Efficiency · settings',
        value: t.efficiency_settings != null ? t.efficiency_settings.toFixed(1) : '–', unit: '%',
        note: `against the workbook's machine settings · ${num(t.target_settings ?? 0)} kg target`,
      },
      {
        label: 'Waste', value: (actual ? ((t.waste ?? 0) / actual) * 100 : 0).toFixed(2), unit: '%',
        note: `${num(t.waste ?? 0)} kg lifted off the floor`,
      },
      ...(showUnit('UNIT - 1') ? [{
        label: 'Unit 1', value: num(u1?.actual_prod ?? 0), unit: 'kg',
        note: `${u1?.total_mcs ?? 0} machines running`,
      }] : []),
      ...(showUnit('UNIT - 2') ? [{
        label: 'Unit 2', value: num(u2?.actual_prod ?? 0), unit: 'kg',
        note: `${u2?.total_mcs ?? 0} machines running`,
      }] : []),
      {
        label: 'Solar share', value: eb + solar ? ((solar / (eb + solar)) * 100).toFixed(1) : '–',
        unit: '%', note: eb + solar ? 'of power drawn · rest from EB' : 'no meter reading',
      },
      {
        label: 'Power per kg yarn', value: cost?.metered ? cost.ukg!.toFixed(2) : '–',
        unit: cost?.metered ? 'units' : '',
        note: cost?.metered
          ? (cost.power != null ? `₹${cost.power.toFixed(2)} power cost per kg` : 'tariffs not set')
          : 'no reliable meter reading',
      },
      {
        label: 'Production cost per kg', value: cost?.total != null ? `₹${cost.total.toFixed(0)}` : '–',
        unit: cost?.total != null ? '/kg' : '',
        note: !cost ? 'needs output'
          : cost.missing.length ? `waiting for ${cost.missing.join(', ')} — not set`
            : cost.metered
              ? `cotton ₹${cost.cotton!.toFixed(0)} · power ₹${cost.power!.toFixed(1)} · other ₹${cost.other!.toFixed(1)}`
              : 'power not metered today',
      },
    ]} />
  )
}

function UnitSplit({ day }: { day: DayDetail }) {
  const rows = ['UNIT - 1', 'UNIT - 2'].filter((name) => !day.unit || day.unit === name).map((name) => {
    const r = day.unit_totals.find((x) => x.unit === name)
    const target = r?.target_prod ?? 0
    const actual = r?.actual_prod ?? 0
    return {
      unit: unitLong(name),
      mcs: r ? String(r.total_mcs) : '–',
      actual: num(actual), target: num(target),
      eff: target ? pct((actual / target) * 100) : '–',
      targetSet: r?.target_settings ? num(r.target_settings) : '–',
      effSet: r?.efficiency_settings != null ? pct(r.efficiency_settings) : '–',
    }
  })
  const t = day.totals
  return (
    <Blueprint className="sheet">
      <PanelHead
        title={longDate(day.date)}
        hint={`${num(t.actual ?? 0)} kg produced · ${pct(t.efficiency ?? 0)} efficiency · ${num(t.waste ?? 0)} kg waste`}
      />
      <div className="scroll-x" style={{ marginTop: 12 }}>
        <table className="table" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th>Unit</th><th style={R}>Machines</th><th style={R}>Actual kg</th>
              <th style={R}>SITRA target</th><th style={R}>Eff. SITRA</th>
              <th style={R}>Settings target</th><th style={R}>Eff. settings</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.unit}>
                <td style={{ fontWeight: 600 }}>{r.unit}</td>
                <td className={NUM} style={R}>{r.mcs}</td>
                <td className={NUM} style={R}>{r.actual}</td>
                <td className={NUM} style={R}>{r.target}</td>
                <td className={NUM} style={R}>{r.eff}</td>
                <td className={NUM} style={R}>{r.targetSet}</td>
                <td className={NUM} style={R}>{r.effSet}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Blueprint>
  )
}

/** Machine time run against installed, output against target, and the two
 *  multiplied — per unit and for the plant. Either factor can look healthy
 *  while the other is poor, which is the whole reason both are shown. */
function UtilAndEfficiency({ day }: { day: DayDetail }) {
  const u = day.utilization
  if (!u?.rows?.length) return null
  return (
    <Blueprint className="sheet">
      <PanelHead title="Utilization and efficiency"
        hint={u.stop_loss ? `${u.stop_loss.toFixed(2)}% stoppage booked` : undefined} />
      <Note>
        Machine time run against installed, output against the workbook's machine-settings
        standard, and the two multiplied. Efficiency against the SITRA target is alongside for
        comparison.
      </Note>
      <div className="scroll-x" style={{ marginTop: 4 }}>
        <table className="table" style={{ minWidth: 620 }}>
          <thead>
            <tr>
              <th>Unit</th><th style={R}>Utilization</th>
              <th style={R}>Eff. settings</th><th style={R}>Util × eff</th>
              <th style={R}>Eff. SITRA</th>
            </tr>
          </thead>
          <tbody>
            {u.rows.map((r) => (
              <tr key={r.unit}>
                <td style={{ fontWeight: 600 }}>
                  {r.unit === 'Both units' ? r.unit : unitLong(r.unit)}
                  <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>
                    {r.machines_on} of {r.machines_installed} machines
                  </div>
                </td>
                <td className={NUM} style={R}>{r.utilization.toFixed(1)}%</td>
                <td className={NUM} style={R}>
                  {r.efficiency_settings != null ? `${r.efficiency_settings.toFixed(1)}%` : '–'}
                </td>
                <td className={NUM} style={{ ...R, fontWeight: 600 }}>
                  {r.util_x_eff != null ? `${r.util_x_eff.toFixed(1)}%` : '–'}
                </td>
                <td className={NUM} style={R}>
                  {r.efficiency != null ? `${r.efficiency.toFixed(1)}%` : '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Blueprint>
  )
}

/** The day count by count, against one standard at a time: the SITRA norm
 *  (the page's own) or the sheet's settings, switched as on the Machine wise
 *  page. Grams per spindle per shift are whole numbers here. */
function ItemsRun({ day }: { day: DayDetail }) {
  const [basis, setBasis] = useState<'sitra' | 'settings'>('sitra')
  if (!day.items.length) return null
  const sitra = basis === 'sitra'
  const g = (v: number | null) => (v == null ? '–' : num(Math.round(v)))
  return (
    <Blueprint className="sheet">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <PanelHead title="Count wise efficiency"
          hint={`${day.items.length} items · against ${sitra ? 'the SITRA norm for the count' : 'the sheet’s settings'}`} />
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="axis-title">Standard</span>
          <Segmented<'sitra' | 'settings'> value={basis} minWidth={88} onChange={setBasis}
            options={[{ value: 'settings', label: 'Settings' }, { value: 'sitra', label: 'SITRA' }]} />
        </div>
      </div>
      <div className="scroll-x" style={{ marginTop: 12 }}>
        <table className="table" style={{ minWidth: 860 }}>
          <thead>
            <tr>
              <th>Item</th><th style={R}>Mcs</th>
              <th style={R}>Actual kg</th><th style={R}>Actual gps</th>
              <th style={R}>Target kg</th><th style={R}>Target gps</th>
              <th style={R}>Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {day.items.map((it, i) => {
              const target = sitra ? it.target_prod : it.target_settings
              const tGps = sitra ? it.t_gps : it.t_gps_settings
              const eff = sitra ? it.efficiency : it.efficiency_settings
              return (
                <tr key={i}>
                  <td>
                    {it.item}
                    <span className="muted" style={{ fontSize: 12 }}> · {unitShort(it.unit)}</span>
                  </td>
                  <td className={NUM} style={R}>{it.no_of_mcs}</td>
                  <td className={NUM} style={R}>{num(it.actual_prod)}</td>
                  <td className={NUM} style={R}>{g(it.a_gps)}</td>
                  <td className={NUM} style={R}>{target ? num(target) : '–'}</td>
                  <td className={NUM} style={R}>{target ? g(tGps) : '–'}</td>
                  <td className={NUM} style={{ ...R, fontWeight: 600 }}>{eff != null ? pct(eff) : '–'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <Note>
        gps is grams per spindle per shift over the spindles on the machines that ran. The SITRA
        target is the count’s industry norm times those spindles; the settings target is the
        sheet’s own standard from the rows’ rpm and TPI. A line with no standard for its count shows no target.
      </Note>
    </Blueprint>
  )
}

/** Outline is the day's target, solid is what actually ran. */
function ItemBars({ day }: { day: DayDetail }) {
  if (!day.items.length) return null
  const max = niceMax(Math.max(
    1, ...day.items.map((it) => Math.max(it.target_prod || 0, it.actual_prod || 0)),
  ))
  const ticks = [0, 1, 2, 3, 4].map((i) => {
    const v = (max * i) / 4
    return { x: i * (BAR_W / 4), label: v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(Math.round(v)) }
  })

  return (
    <Blueprint className="sheet">
      <PanelHead title="Output against target by item" />
      <Note>Outline is the day's target, solid is what ran.</Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {day.items.map((it, i) => {
          const a = it.actual_prod || 0
          const tg = it.target_prod || 0
          return (
            <HoverRow key={i} cols="170px minmax(0,1fr) 110px" tip={
              <>
                <TipTitle>{it.item}</TipTitle>
                <TipRow label={unitLong(it.unit)} value={`${it.no_of_mcs} mcs`} />
                <TipRow label="Actual" value={`${num(a)} kg`} color="var(--c-bar1)" />
                <TipRow label="Target" value={`${num(tg)} kg`} color="var(--c-bar2)" />
                <TipRow label="Of target" value={`${(tg ? (a / tg) * 100 : 0).toFixed(0)}%`} />
                <TipRow label="g/spindle" value={it.a_gps ? it.a_gps.toFixed(1) : '–'} />
              </>
            }>
              <div style={{ fontSize: 12, lineHeight: 1.25 }}>
                {it.item}<span className="muted"> · {unitShort(it.unit)}</span>
              </div>
              <svg viewBox={`0 0 ${BAR_W} 18`} preserveAspectRatio="none"
                style={{ width: '100%', height: 18, display: 'block' }}>
                {ticks.map((t, n) => (
                  <line key={n} x1={t.x} y1="0" x2={t.x} y2="18"
                    stroke="var(--c-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
                <rect x="0" y="0" width={Math.max(1, (tg / max) * BAR_W)} height="18"
                  fill="none" stroke="var(--c-bar2)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                <rect x="0" y="4" width={Math.max(1, (a / max) * BAR_W)} height="10"
                  fill="var(--c-bar1)" />
              </svg>
              <div className={NUM} style={{ fontSize: 12, textAlign: 'right' }}>
                {num(a)} kg
                <div className="muted">{(tg ? (a / tg) * 100 : 0).toFixed(0)}% of target</div>
              </div>
            </HoverRow>
          )
        })}
        <div style={{
          display: 'grid', gridTemplateColumns: '170px minmax(0,1fr) 110px',
          alignItems: 'start', gap: 14, marginTop: 2,
        }}>
          <div />
          <div style={{ minWidth: 0 }}>
            <div className={NUM} style={{
              display: 'flex', justifyContent: 'space-between', minWidth: 0,
              fontSize: 11, color: 'var(--color-neutral-600)',
              borderTop: '1px solid var(--c-grid)', paddingTop: 4,
            }}>
              {ticks.map((t, n) => <span key={n}>{t.label}</span>)}
            </div>
            <div className="axis-title" style={{ textAlign: 'center', marginTop: 5 }}>Kilograms</div>
          </div>
          <div />
        </div>
      </div>
    </Blueprint>
  )
}

/** Stoppage share by reason — Unit 1 dark, Unit 2 light. */
function TimeWent({ day }: { day: DayDetail }) {
  const rows = day.stoppages
    .map((s) => ({ category: s.category, u1: s.unit1_pct || 0, u2: s.unit2_pct || 0 }))
    .sort((a, b) => (b.u1 + b.u2) - (a.u1 + a.u2))
    .slice(0, 8)
  if (!rows.length) return (
    <Empty title="Where the time went">No stoppages were recorded for this day.</Empty>
  )
  const max = Math.max(1, ...rows.map((r) => r.u1 + r.u2))

  return (
    <Blueprint className="sheet">
      <PanelHead title="Where the time went" />
      <Note>Stoppage share by reason — Unit 1 dark, Unit 2 light.</Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {rows.map((r, i) => {
          const w1 = (r.u1 / max) * BAR_W
          const w2 = (r.u2 / max) * BAR_W
          return (
            <HoverRow key={i} cols="170px minmax(0,1fr) 70px" tip={
              <>
                <TipTitle>{r.category}</TipTitle>
                <TipRow label="Unit 1" value={r.u1.toFixed(2) + '%'} color="var(--c-bar3)" />
                <TipRow label="Unit 2" value={r.u2.toFixed(2) + '%'} color="var(--c-bar4)" />
                <TipRow label="Combined" value={(r.u1 + r.u2).toFixed(2) + '%'} />
              </>
            }>
              <div style={{ fontSize: 12, lineHeight: 1.25 }}>{r.category}</div>
              <svg viewBox={`0 0 ${BAR_W} 14`} preserveAspectRatio="none"
                style={{ width: '100%', height: 14, display: 'block' }}>
                <rect x="0" y="0" width={BAR_W} height="14" fill="var(--c-track)" />
                <rect x="0" y="0" width={w1} height="14" fill="var(--c-bar3)" />
                <rect x={w1} y="0" width={w2} height="14" fill="var(--c-bar4)" />
              </svg>
              <div className={NUM} style={{ fontSize: 12, textAlign: 'right' }}>
                {(r.u1 + r.u2).toFixed(2)}%
              </div>
            </HoverRow>
          )
        })}
      </div>
    </Blueprint>
  )
}

function Stoppages({ day }: { day: DayDetail }) {
  return (
    <Blueprint className="sheet">
      <PanelHead title="Stoppages" />
      <table className="table" style={{ marginTop: 10 }}>
        <thead>
          <tr><th>Reason</th><th style={R}>Unit 1 %</th><th style={R}>Unit 2 %</th></tr>
        </thead>
        <tbody>
          {day.stoppages.map((s, i) => (
            <tr key={i}>
              <td>{s.category}</td>
              <td className={NUM} style={R}>{s.unit1_pct != null ? s.unit1_pct.toFixed(2) : '–'}</td>
              <td className={NUM} style={R}>{s.unit2_pct != null ? s.unit2_pct.toFixed(2) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Blueprint>
  )
}

function PowerAndWaste({ day }: { day: DayDetail }) {
  const readings = ['EB', 'Solar', 'Total', 'UKG'].map((k) => {
    const v = day.electricity[k]
    return {
      label: k === 'UKG' ? 'Units per kg' : k,
      value: v == null ? '–' : (k === 'UKG' ? v.toFixed(2) : num(v)),
    }
  })
  return (
    <Blueprint className="sheet">
      <PanelHead title="Power" />
      <table className="table" style={{ marginTop: 10 }}>
        <thead><tr><th>Reading</th><th style={R}>Value</th></tr></thead>
        <tbody>
          {readings.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td className={NUM} style={R}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="eyebrow" style={{ margin: '20px 0 6px' }}>Waste stock</div>
      <table className="table">
        <thead>
          <tr><th>Type</th><th style={R}>Made today</th><th style={R}>In stock</th></tr>
        </thead>
        <tbody>
          {day.waste_stock.map((w, i) => (
            <tr key={i}>
              <td>{w.category}</td>
              <td className={NUM} style={R}>{w.produced ? num(w.produced) : '–'}</td>
              <td className={NUM} style={R}>{num(w.closing)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Blueprint>
  )
}

function YarnStock({ day }: { day: DayDetail }) {
  const total = day.yarn_stock.reduce((a, y) => a + (y.qty || 0), 0)
  return (
    <Blueprint className="sheet">
      <PanelHead title="Yarn in stock" hint={total ? `${compactKg(total)} on hand` : undefined} />
      <table className="table" style={{ marginTop: 10 }}>
        <thead><tr><th>Item</th><th style={R}>Kg</th></tr></thead>
        <tbody>
          {day.yarn_stock.map((y, i) => (
            <tr key={i}>
              <td>{y.item}</td>
              <td className={NUM} style={R}>{num(y.qty)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Blueprint>
  )
}

/** A grid row that shows a hover readout. The bar rows here carry a label
 *  and one number; the tooltip holds the breakdown behind them. */
function HoverRow({ cols, tip, children }: {
  cols: string
  tip: React.ReactNode
  children: React.ReactNode
}) {
  const [over, setOver] = useState(false)
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: cols,
        alignItems: 'center', gap: 14, position: 'relative',
      }}
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
    >
      {children}
      {over ? (
        <div style={{
          position: 'absolute', left: '50%', bottom: '100%', transform: 'translate(-50%, -8px)',
          width: 200, pointerEvents: 'none', zIndex: 5,
          background: 'var(--color-bg)', border: '1px solid var(--color-divider)',
          boxShadow: 'var(--shadow-md)', padding: '8px 10px', fontSize: 12, lineHeight: 1.45,
        }}>{tip}</div>
      ) : null}
    </div>
  )
}
